import { useEffect, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import { RACE_COLORS, FONT_FAMILY, IS_TOUCH, BAR_HEIGHT, BAR_PADDING_AGE } from "../constants.js";

export default function AgePyramid({ data }) {
  const ref = useRef();

  useEffect(() => {
    if (!data || !data.length) return;

    const ageGroups = data.map(d => d.age_group);
    const n = ageGroups.length;
    const height = n * BAR_HEIGHT + BAR_PADDING_AGE;

    // Margins: t:60, b:150. Annotations sit ~50px below x-axis.
    const T = 60, B = 150;
    const plotAreaH = height - T - B;
    const annotY = -(50 / plotAreaH);

    const blackVals = data.map(d => d.black_pct != null ? d.black_pct : null);
    const whiteVals = data.map(d => d.white_pct != null ? -(d.white_pct) : null);

    const maxVal = Math.max(
      ...blackVals.filter(v => v != null),
      ...data.map(d => d.white_pct).filter(v => v != null),
      10
    );
    const axisMax = Math.ceil(maxVal / 5) * 5 + 25;

    const traces = [
      {
        type: "bar",
        orientation: "h",
        name: "BLACK",
        x: blackVals,
        y: ageGroups,
        marker: { color: RACE_COLORS.BLACK },
        text: blackVals.map(v => v != null ? `${v}%` : ""),
        textposition: "outside",
        textfont: { size: 11, family: FONT_FAMILY },
        cliponaxis: false,
        hovertemplate: "<b>BLACK</b><br>%{y}: %{x:.1f}%<extra></extra>",
      },
      {
        type: "bar",
        orientation: "h",
        name: "WHITE",
        x: whiteVals,
        y: ageGroups,
        marker: { color: RACE_COLORS.WHITE },
        text: data.map(d => d.white_pct != null ? `${d.white_pct}%` : ""),
        textposition: "outside",
        textfont: { size: 11, family: FONT_FAMILY },
        cliponaxis: false,
        hovertemplate: "<b>WHITE</b><br>%{y}: %{customdata:.1f}%<extra></extra>",
        customdata: data.map(d => d.white_pct),
      },
    ];

    Plotly.react(ref.current, traces, {
      barmode: "overlay",
      title: {
        text: "<b>Age Range</b>",
        font: { size: 18, color: "#333333", family: FONT_FAMILY },
        x: 0, xanchor: "left",
      },
      height,
      margin: { l: 80, r: 80, t: T, b: B },
      bargap: 0.2,
      xaxis: {
        range: [-axisMax, axisMax],
        tickvals: [-axisMax, -axisMax / 2, 0, axisMax / 2, axisMax],
        ticktext: [
          `${axisMax}%`,
          `${axisMax / 2}%`,
          "0%",
          `${axisMax / 2}%`,
          `${axisMax}%`,
        ],
        tickfont: { size: 11, family: FONT_FAMILY },
        zeroline: true,
        zerolinecolor: "#333",
        zerolinewidth: 2,
      },
      yaxis: {
        tickfont: { size: 12, family: FONT_FAMILY },
        automargin: true,
      },
      annotations: [
        {
          x: -axisMax / 2,
          y: annotY,
          xref: "x",
          yref: "paper",
          text: "<b>← White</b>",
          showarrow: false,
          font: { size: 12, color: RACE_COLORS.WHITE, family: FONT_FAMILY },
        },
        {
          x: 0,
          y: annotY,
          xref: "x",
          yref: "paper",
          text: "% of stops",
          showarrow: false,
          font: { size: 11, color: "#555", family: FONT_FAMILY },
        },
        {
          x: axisMax / 2,
          y: annotY,
          xref: "x",
          yref: "paper",
          text: "<b>Black →</b>",
          showarrow: false,
          font: { size: 12, color: RACE_COLORS.BLACK, family: FONT_FAMILY },
        },
      ],
      font: { family: FONT_FAMILY },
      paper_bgcolor: "white",
      plot_bgcolor: "white",
      showlegend: false,
    }, { responsive: true, displayModeBar: false, staticPlot: IS_TOUCH });
  }, [data]);

  if (!data || !data.length) return null;
  return (
    <div>
      <p style={{ fontFamily: FONT_FAMILY, fontSize: 14, fontStyle: "italic", color: "#555", margin: "4px 0 0" }}>What ages does this officer tend to stop?</p>
      <div className="chart-scroll-x">
        <div ref={ref} style={{ minWidth: 380 }} />
      </div>
    </div>
  );
}
